import React, { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { flexRender } from '@tanstack/react-table'
import cn from 'classnames'

import styles from './styles.module.scss'
import SortIcon from '../../Icons/SortIcon'

const SKELETON_ROW_COUNT = 6
const SKELETON_WIDTHS = ['70%', '55%', '80%', '50%', '65%', '45%']

const Table = forwardRef(({table, columns, placeholder, wrapperStyle = {}, tableClassName, headerClassName, loading = false, onClickRow}, ref) => {
  const { t } = useTranslation()

  const [hoveredRowId, setHoveredRowId] = useState(null)
  const visibleColumns = table.getVisibleLeafColumns()

  return (
    <div className={styles.wrapper} style={wrapperStyle} ref={ref}>
      <table className={cn(styles.table, tableClassName)}>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  className={cn(headerClassName, {[styles.statusColumn]: header.column.id === 'status'})}
                >
                  {header.isPlaceholder ? null : header.column.getCanSort() ? (
                    <button
                      type='button'
                      className={styles.sortButton}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span className={styles.sortLabel}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      <SortIcon
                        className={cn(styles.sortIcon, {
                          [styles.sortIconDesc]: header.column.getIsSorted() === 'desc',
                          [styles.sortIconInactive]: !header.column.getIsSorted(),
                        })}
                      />
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading ? Array.from({length: SKELETON_ROW_COUNT}, (_, rowIndex) => (
            <tr key={`skeleton-${rowIndex}`} className={styles.skeletonRow}>
              {visibleColumns.map((column, columnIndex) => (
                <td
                  key={`${column.id}-${rowIndex}`}
                  className={column.id === 'status' ? styles.statusColumn : ''}
                >
                  <span
                    className={styles.skeletonCell}
                    style={{width: SKELETON_WIDTHS[columnIndex % SKELETON_WIDTHS.length]}}
                  />
                </td>
              ))}
            </tr>
          )) : table.getRowModel().rows.length ? table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              id={row.id}
              className={row.getIsSelected() ? styles.rowSelected : ''}
              onMouseEnter={() => setHoveredRowId(row.id)}
              onMouseLeave={() => setHoveredRowId(null)}
              onClick={onClickRow ? () => onClickRow(row.original) : undefined}>
              {row.getVisibleCells().map(cell => (
                <td
                  key={cell.id}
                  id={cell.id}
                  className={cell.column.id === 'status' ? styles.statusColumn : ''}
                >
                  {flexRender(cell.column.columnDef.cell, {...cell.getContext(), isHovered: hoveredRowId === row.id})}
                </td>
              ))}
            </tr>
          )) : (
            <tr>
              <td colSpan={columns.length} className={styles.emptyState}>
                {
                  placeholder
                    ? placeholder
                    : t('No items found')
                }
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
})

export default Table
